import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import StatsAPI from "@/src/api/StatsAPI";

import { contentViewsQuery } from "./dashboard.queries";
import { buildViewsTimeline } from "../charts/charts.utils";
import ExportCsvButton from "../charts/ExportCsvButton";
import TimelineQueryCard from "../charts/TimelineQueryCard";

import type { JSX } from "react";

import type { GetContentViewsResponse } from "@/src/api/StatsAPI.types";

import type { DashboardPeriodProps } from "./dashboard.types";
import type {
  ChartSeries,
  TimelineDatum,
  ViewsSeriesKey,
} from "../charts/charts.types";

function buildTimeline(
  response: GetContentViewsResponse | undefined,
): TimelineDatum<ViewsSeriesKey>[] {
  return buildViewsTimeline(response?.data ?? []);
}

function ContentViewsTimelineCard({
  period,
}: DashboardPeriodProps): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t } = useTranslation("editor-analytics");

  const seriesList = useMemo(
    (): ChartSeries<ViewsSeriesKey>[] => [
      {
        key: "views",
        label: t("charts.series.views"),
        color: "var(--chart-1)",
      },
    ],
    [t],
  );
  const fetchCsv = async (): Promise<BlobPart> =>
    StatsAPI.getContentViews({
      projectSlug,
      startDate: period.startDate,
      endDate: period.endDate,
      isExport: true,
    });

  return (
    <TimelineQueryCard
      buildTimeline={buildTimeline}
      endDate={period.endDate}
      query={contentViewsQuery(projectSlug, period)}
      seriesList={seriesList}
      startDate={period.startDate}
      title={t("dashboard.charts.contentViews")}
      extraActions={
        <ExportCsvButton fetchCsv={fetchCsv} fileName="views.csv" />
      }
    />
  );
}

export default ContentViewsTimelineCard;
